// src/pages/HR/reports/AttendanceLeave.js
import React, { useMemo, useState } from "react";
import { collection, getDocs, addDoc } from "firebase/firestore";
import { db } from "../../../services/firebase";
import HRCampusSelector from "../../../components/HRCampusSelector";

function exportCSV(filename, rows) {
  const header = ["Date","Employee","Status","Leave Type","Notes","Campus"];
  const body = rows.map(r => [r.date,r.employee,r.status,r.leaveType || "",r.notes || "", r.campus || ""]);
  const csv = [header, ...body].map(r => r.map(x => `"${String(x || "").replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

export default function AttendanceLeave() {
  const [campus, setCampus] = useState(() => localStorage.getItem("hr_reports_campus") || "bulacan");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const scoped = useMemo(() => rows.filter(r => (r.campus || "") === campus), [rows, campus]);
  const ranged = useMemo(() => {
    return scoped.filter(r => {
      const d = new Date(r.date).getTime();
      const okStart = !start || d >= new Date(start).getTime();
      const okEnd = !end || d <= new Date(end).getTime();
      return okStart && okEnd;
    });
  }, [scoped, start, end]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ranged;
    return ranged.filter(r => (r.employee || "").toLowerCase().includes(s) || (r.status || "").toLowerCase().includes(s) || (r.leaveType || "").toLowerCase().includes(s));
  }, [ranged, q]);

  async function reload() {
    const snap = await getDocs(collection(db, "employees_attendance"));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setRows(list);
    localStorage.setItem("hr_reports_campus", campus);
  }
  async function autoAbsent() {
    if (!start || !end) { alert("Set start and end dates."); return; }
    const empSnap = await getDocs(collection(db, "employees"));
    const emps = empSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => String(e.campus || "").toLowerCase() === String(campus).toLowerCase());
    const attSnap = await getDocs(collection(db, "employees_attendance"));
    const atts = attSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => String(a.campus || "") === campus);
    const presentKey = new Set(atts.filter(a => {
      const d = a.date;
      const t = new Date(d).getTime();
      return (!start || t >= new Date(start).getTime()) && (!end || t <= new Date(end).getTime()) && (a.status === "Present" || a.status === "On Leave" || a.status === "Leave");
    }).map(a => `${a.employeeName || a.employee}|${a.date}`));
    const days = [];
    let cur = new Date(start);
    const endD = new Date(end);
    while (cur.getTime() <= endD.getTime()) {
      days.push(cur.toISOString().slice(0,10));
      cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    }
    let created = 0;
    for (const d of days) {
      for (const e of emps) {
        const name = e.name || e.employeeName || "";
        if (!name) continue;
        const key = `${name}|${d}`;
        if (!presentKey.has(key)) {
          await addDoc(collection(db, "employees_attendance"), {
            date: d,
            employeeName: name,
            status: "Absent",
            leaveType: "",
            notes: "",
            campus
          });
          created++;
        }
      }
    }
    alert(`Auto-logged ${created} Absent record(s).`);
    await reload();
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Attendance & Leave</h3>
        <div className="d-flex align-items-center gap-2 print-hide">
          <HRCampusSelector selectedCampus={campus} onChange={setCampus} />
          <input type="date" className="form-control" value={start} onChange={e => setStart(e.target.value)} />
          <input type="date" className="form-control" value={end} onChange={e => setEnd(e.target.value)} />
          <input className="form-control" style={{ width: 220 }} placeholder="Search employee/status" value={q} onChange={e => setQ(e.target.value)} />
          <button className="btn btn-outline-primary" onClick={reload}>Reload</button>
          <button className="btn btn-outline-secondary" onClick={() => window.print()}>Print</button>
          <button className="btn btn-primary" onClick={() => exportCSV(`attendance-leave-${campus}.csv`, filtered)}>Export CSV</button>
          <button className="btn btn-warning" onClick={autoAbsent} title="Fill dates with Absent where no entry exists">Auto‑log Absent</button>
        </div>
      </div>
      <p className="text-muted">Track attendance, leave balances, and absenteeism trends.</p>
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Employee</th>
              <th>Status</th>
              <th>Leave Type</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="5" className="text-center text-muted">No records</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td>{r.employeeName || r.employee}</td>
                <td>{r.status}</td>
                <td>{r.leaveType || "-"}</td>
                <td>{r.notes || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
